// The dashboard's attention inputs (review threads and recent timeline per
// pull request), read from GitHub and kept between loads.
//
// They are the dashboard's costliest GitHub read: a DASH_ATTENTION batch of
// 25 pull requests costs 26 GraphQL points (each pull request's first 100
// threads open a connection for their first comments), so a load of 100
// rows costs about 100 of the hourly 5,000. Kept inputs are used again only
// after DASH_ATTENTION_CHECK (about 1 point per 100 pull requests) shows
// that nothing they depend on changed: the pull request's own `updatedAt`
// (commits, reviews, comments, review requests, state changes) and its
// threads' resolved states (resolving a thread does not move `updatedAt`).
// A pull request is also forgotten on every event the account's
// invalidation log (runtime/cache.ts, `useLog`) hears for it, from any
// tab: a notice that named it (`forget`), the app's writes to it and the
// viewer opening it (the pull request page). All of them are recorded by
// one producer (runtime/appLog.ts); these inputs only listen. An event
// reaches the app's other tabs and outlives the page, and kept data that
// depends on the pull request (the saved dashboard, these inputs as
// stored) is dropped wherever it is read.
//
// A read that began before its pull request was forgotten, or under another
// signed-in account, is not kept (`begin`/`accept`).
import { graphql, RateLimitError } from '../api/gh';
import { identityGeneration, onIdentityChange } from '../api/identity';
import { DASH_ATTENTION, DASH_ATTENTION_CHECK, type DashAttentionCheck, type DashAttentionInputs, type DashPr } from '../api/queries';
import { appLog, AppLog, changeOf, prKeyOf } from '../runtime/appLog';
import type { InvalidationLog, InvalidationSource } from '../runtime/cache';

export { prKeyOf };

/** Longest kept inputs are used without a full read, even when the check finds no change. */
export const INPUTS_TTL_MS = 30 * 60_000;

/** Entries kept at most (the least recently read go first). */
export const MAX_ENTRIES = 500;

/** Pull requests per DASH_ATTENTION request. */
export const ATTENTION_BATCH = 25;

/** Pull requests per DASH_ATTENTION_CHECK request. */
export const CHECK_BATCH = 100;

/** One pull request's kept inputs. */
export interface KeptInputs {
  /** `owner/name#number`, lowercased. */
  key: string;
  /** The pull request's own `updatedAt` when read. */
  updatedAt: string;
  /** Its threads' resolved states when read, one character each. */
  resolved: string;
  inputs: DashAttentionInputs;
  /** When it was read (epoch ms). */
  at: number;
  /** When that read began (epoch ms): its basis where it is stored; absent in entries kept before this was recorded. */
  basis?: number;
}

/**
 * Where entries persist between runs (the extension's service worker is
 * stopped between refreshes; the app's page is reloaded). Entries are
 * stored one by one: a save writes only those that changed.
 */
export interface InputsStore {
  /** Every entry stored for the signed-in account, by node ID. */
  read(): Promise<Record<string, KeptInputs> | undefined>;
  /** Store these entries, and delete those given as null; the rest stay as they are. Rejects when they could not be stored. */
  write(changes: Record<string, KeptInputs | null>): Promise<void>;
}

/** A notice named the pull request: the source of a forget unless another is given. */
const NOTICE: InvalidationSource = { source: 'notice', kind: 'pr' };

/**
 * When a read began: among the forgets this page has heard (`order`, a
 * count that only grows, so no step of the device's clock reorders them),
 * by the clock (`basis`, epoch ms: what a stored entry is compared by, in
 * a later page or against the log), and the signed-in account then.
 */
export interface ReadTicket {
  order: number;
  basis: number;
  generation: number;
}


const resolvedOf = (threads: { nodes: { isResolved: boolean }[] }) => threads.nodes.map((t) => (t.isResolved ? '1' : '0')).join('');

export class AttentionInputs {
  private entries = new Map<string, KeptInputs>();
  private loaded: Promise<void> | null = null;
  /** When each key was last forgotten, and when everything last was (epoch ms; see `changedSince`). */
  private forgottenAt = new Map<string, number>();
  private clearedTime = 0;
  /** Counts the forgets heard; `forgottenOrder` holds the count at each key's latest one. */
  private order = 0;
  private forgottenOrder = new Map<string, number>();
  /** The count when everything kept was last dropped (`clear`), or the forgets' own record was: reads begun before it are not kept. */
  private resetOrder = 0;
  private changedListeners = new Set<(key: string) => void>();
  /** The account's invalidation log, while one is given (`useLog`). */
  private log: InvalidationLog | null = null;
  private unhook: (() => void) | null = null;
  /** Entries changed since they were last persisted, by node ID: null for one deleted. */
  private changes = new Map<string, KeptInputs | null>();

  /**
   * `store` persists entries between runs; `log` carries forgets (see the
   * file comment); `producer` records the forgets made here (its own
   * unless given: the app's shared one).
   */
  constructor(
    private store: InputsStore | null = null,
    log: InvalidationLog | null = null,
    private readonly producer: AppLog = new AppLog(),
  ) {
    // What is made before a log is given is dropped here at once.
    producer.onWaiting((key, source) => this.heard(key, source));
    this.useLog(log);
  }

  /** Persist entries in `store` from now on, and read those it holds before the next lookup. */
  persistIn(store: InputsStore) {
    if (this.store === store) return;
    this.store = store;
    this.loaded = null;
  }

  /**
   * Forget what `log`'s events name from now on, whichever tab made them,
   * and have the producer record in it; null: forgets stay in this page.
   */
  useLog(log: InvalidationLog | null) {
    this.unhook?.();
    this.unhook = null;
    this.log = log;
    if (log) this.unhook = log.subscribe((e) => this.heard(e.subject, e));
    // After listening: events made before the log opened are recorded now, and heard.
    this.producer.use(log);
  }

  /**
   * Call `listener` with each key whose event the log could not keep (not
   * stored, or only in this page's memory): a page opened later would not
   * know of it, so what is kept for the key elsewhere must go.
   */
  onForgetLost(listener: (key: string) => void): () => void {
    return this.producer.onLost(listener);
  }

  /**
   * Whether `repo#number` was forgotten at or after `at` (epoch ms): in the
   * log (any tab, or before this page opened) when there is one, else in
   * this page. What was worked out from it by then no longer holds. When
   * too many forgets were recorded to keep each, every key counts as
   * forgotten since the oldest was dropped. (`clear` does not count: it
   * marks another account, whose saved data lives elsewhere; see api/db.ts.)
   */
  changedSince(key: string, at: number): boolean {
    if (this.log?.changedSince(key, at)) return true;
    return this.clearedTime >= at || (this.forgottenAt.get(key) ?? -Infinity) >= at;
  }

  /** Read the persisted entries once, before the first lookup. */
  private load(): Promise<void> {
    this.loaded ??= (async () => {
      try {
        const saved = await this.store?.read();
        for (const [id, e] of Object.entries(saved ?? {}))
          if (!this.entries.has(id) && !this.forgottenOrder.has(e.key) && !this.changedSince(e.key, e.basis ?? e.at)) this.entries.set(id, e);
      } catch {
        /* no saved entries: read them from GitHub */
      }
    })();
    return this.loaded;
  }

  /** Mark the start of a read whose answers may be kept (see `accept`). */
  begin(): ReadTicket {
    return { order: this.order, basis: Date.now(), generation: identityGeneration() };
  }

  /** The kept entries for `prs` that are young enough to check, by node ID. */
  async candidates(prs: DashPr[], now = Date.now()): Promise<Map<string, KeptInputs>> {
    await this.load();
    const out = new Map<string, KeptInputs>();
    for (const p of prs) {
      const e = this.entries.get(p.id);
      if (!e) continue;
      // A clock that stepped back makes the age negative: not fresh.
      const age = now - e.at;
      if (age < 0 || age >= INPUTS_TTL_MS) {
        this.entries.delete(p.id);
        this.changes.set(p.id, null);
      } else out.set(p.id, e);
    }
    return out;
  }

  /** Whether `e` still holds, by a DASH_ATTENTION_CHECK answer. */
  holds(e: KeptInputs, check: DashAttentionCheck): boolean {
    return check.updatedAt === e.updatedAt && resolvedOf(check.reviewThreads) === e.resolved;
  }

  /**
   * Keep inputs read for `pr` by a read that began at `ticket`, unless the
   * pull request was forgotten since (a forget this page heard after the
   * ticket was taken, by count, or one the log dates at or after it),
   * everything was dropped since, or the signed-in account changed.
   */
  accept(ticket: ReadTicket, pr: DashPr, inputs: DashAttentionInputs, now = Date.now()) {
    const key = prKeyOf(pr.repository.nameWithOwner, pr.number);
    if (ticket.generation !== identityGeneration() || ticket.order < this.resetOrder || (this.forgottenOrder.get(key) ?? 0) > ticket.order) return;
    if (this.log?.changedSince(key, ticket.basis)) return;
    const entry: KeptInputs = {
      key,
      updatedAt: inputs.updatedAt ?? pr.updatedAt,
      resolved: resolvedOf(inputs.reviewThreads),
      inputs,
      at: now,
      basis: ticket.basis,
    };
    this.entries.delete(pr.id);
    this.entries.set(pr.id, entry);
    this.changes.set(pr.id, entry);
    while (this.entries.size > MAX_ENTRIES) {
      const oldest = this.entries.keys().next().value!;
      this.entries.delete(oldest);
      this.changes.set(oldest, null);
    }
  }

  /**
   * Drop what is kept for `repo#number`, for `source` (a notice unless
   * given): here at once, and, through the log, in the other tabs and in
   * what is stored. Resolves to whether the log kept it (true without one).
   */
  forget(repo: string, number: number, source: InvalidationSource = NOTICE): Promise<boolean> {
    // The log applies the event at once, and `drop` hears it like any tab's; without one, the producer says it waits.
    return this.producer.record(source, repo, number);
  }

  /**
   * Call `listener` with each key (`prKeyOf`) as what is kept for it stops
   * holding: forgotten here, or changed in any tab (the log's events).
   * What was worked out from it by then no longer holds either.
   */
  onChanged(listener: (key: string) => void): () => void {
    this.changedListeners.add(listener);
    return () => this.changedListeners.delete(listener);
  }

  /** An event for `subject`: a change to the pull request itself, or one of unknown kind, drops what is kept (review marks change no input). */
  private heard(subject: string, source: InvalidationSource) {
    switch (changeOf(source)) {
      case 'pull-request':
      // Another build's event: it may have changed anything about its subject.
      case 'unknown':
        return this.drop(subject);
      case 'marks':
        return;
    }
  }

  private drop(key: string) {
    this.forgottenOrder.set(key, ++this.order);
    this.forgottenAt.set(key, Date.now());
    for (const [id, e] of this.entries)
      if (e.key === key) {
        this.entries.delete(id);
        this.changes.set(id, null);
      }
    if (this.forgottenAt.size > 2 * MAX_ENTRIES) {
      // Old forgets matter only to reads begun before them; reads that old are dropped wholesale.
      this.forgottenAt.clear();
      this.clearedTime = Date.now();
      this.forgottenOrder.clear();
      this.resetOrder = this.order;
    }
    for (const l of [...this.changedListeners]) l(key);
  }

  /**
   * Drop everything kept, and every read in flight: another account signed
   * in. Nothing is written for the one before (its store is its own, and is
   * deleted); the next lookup reads what is stored for the new one. The
   * previous account's log is let go of.
   */
  clear() {
    this.useLog(null);
    this.producer.clear();
    this.entries.clear();
    this.changes.clear();
    this.resetOrder = ++this.order;
    this.loaded = null;
  }

  /** Persist the entries that changed since the last save, when there is a store. */
  async save(): Promise<void> {
    if (!this.store || !this.changes.size) return;
    const batch = this.changes;
    this.changes = new Map();
    try {
      await this.store.write(Object.fromEntries(batch));
    } catch {
      // The next save tries again, for those not changed since.
      for (const [id, e] of batch) if (!this.changes.has(id)) this.changes.set(id, e);
    }
  }
}

/**
 * The app's inputs, shared by every dashboard the page shows; kept in
 * this browser between page loads, and in step across its tabs, once the
 * session wires them (`dashboardStorage`).
 */
export const sharedAttentionInputs = new AttentionInputs(null, null, appLog);
// Another user may not see the same pull requests, nor the same drafts.
onIdentityChange(() => sharedAttentionInputs.clear());

export interface ReadResult {
  inputs: Map<string, DashAttentionInputs>;
  /** Node IDs whose read, or whose kept inputs' check, failed, with the error. */
  failed: Map<string, unknown>;
  /** Of `failed`, those whose check failed: kept inputs not confirmed, and not read in full either. */
  unchecked: Set<string>;
}

/** What `keptInputs` found: the inputs that still hold, and the rows whose check failed. */
export interface KeptResult {
  ticket: ReadTicket;
  inputs: Map<string, DashAttentionInputs>;
  failed: Map<string, unknown>;
}

export interface ReadOptions {
  /** Use no kept inputs (Try again). */
  fresh?: boolean;
  /** Abandons the reads: waiting requests are dropped unsent. */
  signal?: AbortSignal;
  /** Called when a request met a rate limit, with it. */
  onRateLimit?: (e: RateLimitError) => void;
}

/**
 * The kept inputs for `prs` that DASH_ATTENTION_CHECK shows still hold
 * (none when `fresh`), by node ID, and the ticket to `accept` later reads
 * of the others with. When a check fails, its pull requests are in
 * `failed` with the error and are not to be read in full: a check fails
 * when GitHub is slow, failing or limiting, and full reads are the
 * heaviest requests to send it then. Their kept inputs stay for the next
 * check.
 */
export async function keptInputs(prs: DashPr[], kept: AttentionInputs, opts: ReadOptions = {}): Promise<KeptResult> {
  const ticket = kept.begin();
  const inputs = new Map<string, DashAttentionInputs>();
  const failed = new Map<string, unknown>();
  if (opts.fresh) return { ticket, inputs, failed };
  const held = await kept.candidates(prs);
  const ids = [...held.keys()];
  const checks: Promise<void>[] = [];
  for (let i = 0; i < ids.length; i += CHECK_BATCH) {
    const batch = ids.slice(i, i + CHECK_BATCH);
    checks.push(
      graphql<{ nodes: (DashAttentionCheck | null)[] }>(DASH_ATTENTION_CHECK, { ids: batch }, { signal: opts.signal }).then(
        (res) => {
          for (const n of res.nodes) {
            const e = n?.id ? held.get(n.id) : undefined;
            if (n?.reviewThreads && e && kept.holds(e, n)) inputs.set(n.id, e.inputs);
          }
        },
        (e) => {
          for (const id of batch) failed.set(id, e);
          if (e instanceof RateLimitError) opts.onRateLimit?.(e);
        },
      ),
    );
  }
  await Promise.all(checks);
  return { ticket, inputs, failed };
}

/**
 * Read the inputs of `prs` (at most ATTENTION_BATCH) in one DASH_ATTENTION
 * request, keeping them under `ticket`. `failed` holds them all, with the
 * error, when it failed; a pull request GitHub returned without its node,
 * threads or timeline (a per-node error) is in neither map.
 */
export async function readBatch(prs: DashPr[], kept: AttentionInputs, ticket: ReadTicket, opts: ReadOptions = {}): Promise<ReadResult> {
  const inputs = new Map<string, DashAttentionInputs>();
  const failed = new Map<string, unknown>();
  const unchecked = new Set<string>();
  const byId = new Map(prs.map((p) => [p.id, p]));
  try {
    const res = await graphql<{ nodes: (DashAttentionInputs | null)[] }>(
      DASH_ATTENTION,
      { ids: prs.map((p) => p.id) },
      // GitHub charges about a point per pull request for their timelines and threads.
      { signal: opts.signal, cost: 1 + prs.length },
    );
    for (const n of res.nodes)
      if (n?.id && n.timelineItems && n.reviewThreads) {
        inputs.set(n.id, n);
        const p = byId.get(n.id);
        if (p) kept.accept(ticket, p, n);
      }
  } catch (e) {
    for (const p of prs) failed.set(p.id, e);
    if (e instanceof RateLimitError) opts.onRateLimit?.(e);
  }
  return { inputs, failed, unchecked };
}

/**
 * Attention inputs for `prs`, by node ID: kept ones that still hold
 * (`keptInputs`), the rest read in ATTENTION_BATCH batches (`readBatch`).
 * Rows whose check failed are not read (`unchecked`).
 */
export async function readInputs(prs: DashPr[], kept: AttentionInputs, opts: ReadOptions = {}): Promise<ReadResult> {
  const { ticket, inputs, failed } = await keptInputs(prs, kept, opts);
  const unchecked = new Set(failed.keys());
  const misses = prs.filter((p) => !inputs.has(p.id) && !failed.has(p.id));
  const batches: DashPr[][] = [];
  for (let i = 0; i < misses.length; i += ATTENTION_BATCH) batches.push(misses.slice(i, i + ATTENTION_BATCH));
  await Promise.all(
    batches.map(async (batch) => {
      const r = await readBatch(batch, kept, ticket, opts);
      for (const [id, n] of r.inputs) inputs.set(id, n);
      for (const [id, e] of r.failed) failed.set(id, e);
    }),
  );
  return { inputs, failed, unchecked };
}
